import { styled } from '@material-ui/core';

import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import { common, createLowlight } from 'lowlight';

import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../hooks';
import { blockIdAttribute } from '../utils';
import { MermaidRenderer } from './MermaidRenderer';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from './editorStyles';

const lowlight = createLowlight(common);

const StyledPre = styled('pre')({
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,
  padding: 'var(--bui-space-6)',
  borderRadius: 'var(--bui-radius-3)',
  overflow: 'auto',
  fontFamily: 'var(--bui-font-monospace)',
  fontSize: 'var(--bui-font-size-2)',
  lineHeight: 1.6,
  backgroundColor: '#333333', // gray20
  color: '#f8f8f8', // gray95

  // Base syntax highlighting colors (GitHub Dark theme)
  '& .hljs-doctag, & .hljs-keyword, & .hljs-meta .hljs-keyword, & .hljs-template-tag, & .hljs-template-variable, & .hljs-type, & .hljs-variable.language_':
    {
      color: '#ff7b72',
    },
  '& .hljs-title, & .hljs-title.class_, & .hljs-title.class_.inherited__, & .hljs-title.function_':
    {
      color: '#d2a8ff',
    },
  '& .hljs-attr, & .hljs-attribute, & .hljs-literal, & .hljs-meta, & .hljs-number, & .hljs-operator, & .hljs-variable, & .hljs-selector-attr, & .hljs-selector-class, & .hljs-selector-id':
    {
      color: '#79c0ff',
    },
  '& .hljs-regexp, & .hljs-string, & .hljs-meta .hljs-string': {
    color: '#a5d6ff',
  },
  '& .hljs-built_in, & .hljs-symbol': {
    color: '#ffa657',
  },
  '& .hljs-comment, & .hljs-code, & .hljs-formula': {
    color: '#8b949e',
  },
  '& .hljs-name, & .hljs-quote, & .hljs-selector-tag, & .hljs-selector-pseudo':
    {
      color: '#7ee787',
    },
  '& .hljs-subst': {
    color: '#c9d1d9',
  },
  '& .hljs-section': {
    color: '#1f6feb',
    fontWeight: 'bold',
  },
  '& .hljs-bullet': {
    color: '#f2cc60',
  },
  '& .hljs-emphasis': {
    color: '#c9d1d9',
    fontStyle: 'italic',
  },
  '& .hljs-strong': {
    color: '#c9d1d9',
    fontWeight: 'bold',
  },
  '& .hljs-addition': {
    color: '#aff5b4',
    backgroundColor: '#033a16',
  },
  '& .hljs-deletion': {
    color: '#ffdcd7',
    backgroundColor: '#67060c',
  },
});

const CodeBlockComponent = ({ node, updateAttributes }: NodeViewProps) => {
  const { blockId, language } = node.attrs;
  const zoneId = useBlockZoneId('code-block', blockId, updateAttributes);
  const isMermaid = language === 'mermaid';

  const content = isMermaid ? (
    <MermaidRenderer code={node.textContent} />
  ) : (
    <StyledPre>
      {/* @ts-expect-error - NodeViewContent accepts "code" as a valid element type */}
      <NodeViewContent as="code" />
    </StyledPre>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const CodeBlock = CodeBlockLowlight.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      blockId: blockIdAttribute,
    };
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(CodeBlockComponent);
  },

  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
    };
  },
}).configure({
  lowlight,
  enableTabIndentation: true,
});
